/* eslint-disable no-constant-condition */
/* eslint-disable react/no-danger */

import React, { FC } from "react";

import log from "loglevel";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Typography } from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import { BugFixing, RequestSuccessImage, ScopeTokenExpired, SystemClock, WarningImage } from "@/assets/images";
import { sanitizeHtml } from "@/core/security";
import { AppRoute } from "@/shared/domain";
import { SCOPE_TOKEN_ALREADY_USED, SCOPE_TOKEN_EXPIRED, SupportedErrorCode, TIME_ERROR } from "@/shared/errors";
import { xCorrelationIdProvider } from "@/shared/hooks/useAuth/providers";

import { StyledErrorDialog } from "./styled";
import { getErrorMarkup, isWarning } from "./utils";

export type ErrorDialogProps = {
    open: boolean;
    errorCode?: SupportedErrorCode;
    redirectToBase?: boolean;
    onClose: () => void;
    confirmButton: string;
};

const ErrorDialog: FC<ErrorDialogProps> = ({ open, errorCode, redirectToBase, onClose, confirmButton }) => {
    const { t } = useTranslation();

    const navigate = useNavigate();
    const xCorrelationId = xCorrelationIdProvider.getXCorrelationId();

    const handleConfirm = () => {
        log.info("handleConfirm, redirectToBase", redirectToBase);
        if (redirectToBase) {
            navigate(AppRoute.Root, { replace: true });
        }
        onClose();
    };

    let errorTitle = t("Errors.Title", { context: errorCode?.toString() });
    if (errorTitle.includes("Errors.Title")) {
        errorTitle = t("Errors.Title"); // fallback
    }
    const getIllustration = () => {
        if (errorCode?.toString() === SCOPE_TOKEN_EXPIRED) {
            return <ScopeTokenExpired />;
        } else if (errorCode?.toString() === SCOPE_TOKEN_ALREADY_USED) {
            return <RequestSuccessImage />;
        } else if (errorCode?.toString() === TIME_ERROR) {
            return <SystemClock />;
        } else if (isWarning(errorCode)) {
            return <WarningImage />;
        }
        return <BugFixing />;
    };

    return (
        <StyledErrorDialog
            classes={{ paper: "dialog-paper" }}
            data-testid="dialogError"
            open={open}
            className="errorDialog"
        >
            <div className="errorIcon">{getIllustration()}</div>
            <DialogTitle classes={{ root: "dialog-title-root" }}>
                <Typography className="errorTitle">{errorTitle}</Typography>
            </DialogTitle>
            <DialogContentText className="errorMessage" id="alert-login-error">
                <span
                    style={{ display: "block" }}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(getErrorMarkup(t, errorCode)) }}
                />
                {!isWarning(errorCode) && (
                    <span style={{ display: "block" }}>
                        {t("Errors.ErrorId")}: {xCorrelationId}
                    </span>
                )}
            </DialogContentText>
            <DialogActions classes={{ root: "dialog-actions-root" }} style={{ justifyContent: "center" }}>
                <Button data-testid="confirm-error" onClick={handleConfirm} fullWidth>
                    {confirmButton}
                </Button>
            </DialogActions>
        </StyledErrorDialog>
    );
};

export default ErrorDialog;
